import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, TextField, Typography } from '@mui/material';
import Box from '@mui/system/Box';
import moment from 'moment';
import 'moment/locale/es';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TimerTable } from './TimerTable';
import { LevelTable } from './LevelTable';
import { CodeTable } from './CodeTable';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import { getUrlByPublicId } from './Utils';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import ButtonV2 from '../../atoms/v2/Button'
moment.locale('es');

const urlString = yup.string().url();

const CheckoutPageHandler = (props: ICheckoutPageHandlerProps) => {

    const {
        onClose,
        onSubmit,
        open,
        type,
        payload
    } = props;
    const [videoURL, setVideoURL] = useState<string>('');
    const [totalPrice, setTotalPrice] = useState<string>('');
    const [levels, setLevels] = useState<ILevel[]>([]);
    const [timers, setTimers] = useState<ITimer[]>([]);
    const [codes, setCodes] = useState<ICode[]>([]);
    const [buttonTitle, setButtonTitle] = useState<string>('');
    const [quillContent, setQuillContent] = useState<any>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [editorRef, setEditorRef] = useState<HTMLDivElement | null>(null);
    const [editorVisualizerRef, setEditorVisualizerRef] = useState<HTMLDivElement | null>(null);

    const [quillEditorRef, setQuillEditorRef] = useState<Quill | null>(null);

    useEffect(() => {
        if (editorRef !== null) {
            let quillMain = new Quill(editorRef, {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
                        ['blockquote', 'code-block'],

                        [{ 'header': 1 }, { 'header': 2 }],               // custom button values
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
                        [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent
                        [{ 'direction': 'rtl' }],                         // text direction

                        [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
                        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

                        [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
                        [{ 'font': [] }],
                        [{ 'align': [] }],

                        ['clean']                                         // remove formatting button
                    ]
                },
                theme: 'snow',
                placeholder: "Descripción del botón",
            });

            setQuillEditorRef(quillMain);

            quillMain.on('text-change', function (delta, oldDelta, source) {
                setQuillContent(quillMain.getContents());
            });
        }
    }, [editorRef]);

    useEffect(() => {
        if (editorVisualizerRef !== null) {
            let quill = new Quill(editorVisualizerRef, {
                readOnly: true
            });

            quill.setContents(quillContent);
        }
    }, [quillContent, editorVisualizerRef]);

    useEffect(() => {
        if (quillEditorRef === null) return;
        if (payload === undefined) quillEditorRef.setContents([] as any);
        else quillEditorRef.setContents(JSON.parse(payload.buttonDescription || "[]"));
    }, [payload, quillEditorRef]);

    useEffect(() => {
        if (isLoading) return;
        if (payload === undefined) {
            setVideoURL('');
            setTotalPrice('');
            setLevels([]);
            setTimers([]);
            setCodes([]);
            setButtonTitle('');
        } else {
            setVideoURL(payload.marketingVideoURL || '');
            setTotalPrice(payload.totalPrice || '');
            setLevels(payload.course.levels);
            setTimers(payload.timers);
            setCodes(payload.codes);
            setButtonTitle(payload.buttonTitle || '');
        }
    }, [payload, open, isLoading]);

    const isVideoURLValid = useMemo(() =>
        urlString.isValidSync(videoURL),
        [videoURL]);

    const isDisabled = payload === undefined || isVideoURLValid === false;

    const buttonTitleFormatted = buttonTitle === '' ? 'Inscribirme' : buttonTitle;
    const handleOnSubmit = async () => {
        if (isDisabled) return;

        try {
            setIsLoading(true);

            await onSubmit({
                publicId: payload.publicId,
                marketingVideoURL: videoURL,
                totalPrice: totalPrice,
                timers: timers,
                codes: codes,
                course: {
                    id: payload.course.id,
                    name: payload.course.name,
                    levels: levels
                },
                buttonTitle: buttonTitleFormatted,
                buttonDescription: JSON.stringify(quillContent),
                updatedAt: new Date().toISOString(),
            });

            onClose();
        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Dialog
            maxWidth="lg"
            open={open}
        >
            {type === 'NEW' && <DialogTitle>Agregar página</DialogTitle>}
            {type === 'EDIT' && <DialogTitle>Actualizar página</DialogTitle>}
            <DialogContent dividers>
                <Stack spacing={5}>
                    {payload !== undefined && (
                        <Stack spacing={1}>
                            <Typography variant="h6">
                                Curso
                            </Typography>
                            <Typography variant="subtitle1">
                                {payload.course.name}
                            </Typography>
                        </Stack>
                    )}
                    {(
                        payload !== undefined &&
                        type === 'EDIT'
                    ) && (
                            <Stack spacing={1}>
                                <Typography variant="h6">
                                    Página
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1, sm: 2 }}
                                >
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={<ContentCopyIcon />}
                                        onClick={() => {
                                            navigator.clipboard.writeText(getUrlByPublicId(payload.publicId));
                                        }}
                                        sx={{
                                            m: 1
                                        }}
                                    >
                                        Copiar
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={<OpenInNewIcon />}
                                        href={getUrlByPublicId(payload.publicId)}
                                        target='_blank'
                                    >
                                        Ir a la página
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    <Stack spacing={1}>
                        <Typography variant="h6">
                            Precio
                        </Typography>
                        <Typography variant="subtitle1">
                            Define el precio total del curso
                        </Typography>
                        <TextField
                            variant="outlined"
                            label="Precio"
                            value={totalPrice}
                            onChange={(e) => setTotalPrice(e.target.value)}
                            InputProps={{
                                inputComponent: NumericFormatCustom as any,
                            }}
                        />
                    </Stack>
                    <Stack spacing={1}>
                        <Typography variant="h6">
                            Duración
                        </Typography>
                        <Typography variant="subtitle1">
                            Define la duración por cada nivel del curso
                        </Typography>
                        <LevelTable data={levels} onChange={setLevels} />
                    </Stack>
                    <Stack spacing={1}>
                        <Typography variant="h6">
                            Temporizadores
                        </Typography>
                        <Typography variant="subtitle1">
                            Define los descuentos que se aplicarán por intervalos de tiempo
                        </Typography>
                        <TimerTable data={timers} onChange={setTimers} />
                    </Stack>
                    <Stack spacing={1}>
                        <Typography variant="h6">
                            Códigos de descuento
                        </Typography>
                        <Typography variant="subtitle1">
                            Define los códigos de descuento
                        </Typography>
                        <CodeTable data={codes} onChange={setCodes} />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack spacing={2} direction='row'>
                    <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
                    {type === 'EDIT' &&
                        <LoadingButton
                            loading={isLoading}
                            onClick={handleOnSubmit}
                            disabled={isDisabled}>
                            Guardar
                        </LoadingButton>}
                    {type === 'NEW' &&
                        <LoadingButton
                            loading={isLoading}
                            onClick={handleOnSubmit}
                            disabled={isDisabled}>
                            Agregar
                        </LoadingButton>}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

interface CustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
    function NumericFormatCustom(props, ref) {
        const { onChange, ...other } = props;

        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
                thousandSeparator
                valueIsNumericString
                prefix="$"
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale={true}
            />
        );
    },
);

export default CheckoutPageHandler;